/**
 * TrackingBase - Abstract base class for all analytics integrations.
 * TrackingIntegrationManager - Manages multiple integrations.
 *
 * Standalone version for co-aurora (no build system).
 * Ported from shopper/src/scripts/lib/integrations/base.js
 *
 * All integrations extend window.TrackingBase and register with
 * window.trackingManager (an instance of TrackingIntegrationManager).
 */
(function() {
  'use strict';

  /* ================================================================
   *  TrackingBase
   * ================================================================ */

  function TrackingBase(config) {
    if (this.constructor === TrackingBase) {
      throw new Error('TrackingBase is abstract and cannot be instantiated directly');
    }

    this.config = Object.assign({
      enabled: true,
      debug: false,
      debounceTime: 300,
      retryAttempts: 3,
      retryDelay: 1000,
      queueEnabled: true,
      maxQueueSize: 100,
      tagWaitTime: 7000,
      tagCheckInterval: 100,
      enableDeduplication: true
    }, config || {});

    this.name = this.config.name || 'Unknown';
    this.isInitialized = false;
    this.eventQueue = [];
    this.pendingEvents = new Set();
    this.interceptor = null;
    this.tagLoaded = false;
    this._eventDebounce = new Map();

    if (this.config.enabled) {
      this._initialize();
    }
  }

  /* Abstract methods */
  TrackingBase.prototype.initialize = function() {
    throw new Error(this.name + ': initialize() must be implemented');
  };

  TrackingBase.prototype.track = function(/* eventName, eventData, options */) {
    throw new Error(this.name + ': track() must be implemented');
  };

  /* Internal init wrapper */
  TrackingBase.prototype._initialize = function() {
    var self = this;
    Promise.resolve()
      .then(function() { return self.initialize(); })
      .then(function() {
        self.isInitialized = true;
        self._processQueue();
        self.log('Initialized successfully');
      })
      .catch(function(err) {
        self.error('Failed to initialize', err);
        self.isInitialized = false;
      });
  };

  /* ---- Tag waiting ---- */

  TrackingBase.prototype.waitForTag = function(globalVar, tagName) {
    var self = this;
    if (window[globalVar]) {
      this.tagLoaded = true;
      this.log(tagName + ' already loaded');
      return Promise.resolve(true);
    }

    return new Promise(function(resolve) {
      var attempts = 0;
      var max = Math.floor(self.config.tagWaitTime / self.config.tagCheckInterval);

      (function check() {
        attempts++;
        if (window[globalVar]) {
          self.tagLoaded = true;
          self.log(tagName + ' loaded after ' + (attempts * self.config.tagCheckInterval) + 'ms');
          resolve(true);
          return;
        }
        if (attempts >= max) {
          self.warn(tagName + ' not loaded after ' + self.config.tagWaitTime + 'ms');
          resolve(false);
          return;
        }
        setTimeout(check, self.config.tagCheckInterval);
      })();
    });
  };

  /* ---- Event ID ---- */

  TrackingBase.prototype.generateEventId = function() {
    return this.name.toLowerCase() + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  /* ---- Data normalization ---- */

  TrackingBase.prototype.normalizeEventData = function(eventData) {
    var normalized = Object.assign({}, eventData || {});

    if (normalized.currency) {
      normalized.currency = normalized.currency.toUpperCase();
    } else if (window.theme && window.theme.currency) {
      normalized.currency = window.theme.currency;
    }

    if (normalized.value && typeof normalized.value === 'string') {
      normalized.value = parseFloat(normalized.value);
    }

    if (!normalized.timestamp) normalized.timestamp = Date.now();

    normalized.page_url = window.location.href;
    normalized.page_title = document.title;
    normalized.referrer = document.referrer;

    /* Attribution enrichment */
    if (window.AttributionManager) {
      var attr = window.AttributionManager.getAttribution();
      if (attr.utm) {
        Object.keys(attr.utm).forEach(function(key) {
          if (attr.utm[key]) normalized['utm_' + key] = attr.utm[key];
        });
      }
      if (attr.session) {
        if (attr.session.landing_page) normalized.landing_page = attr.session.landing_page;
        if (attr.session.referrer) normalized.original_referrer = attr.session.referrer;
      }
    }

    return normalized;
  };

  /* ---- Customer data ---- */

  TrackingBase.prototype.getCustomerData = function() {
    if (!window.theme || !window.theme.customer) return {};
    var c = window.theme.customer;
    var d = {};
    if (c.id) d.customer_id = c.id;
    if (c.email) d.email = c.email;
    if (c.phone) d.phone = c.phone;
    if (c.b2b !== undefined) d.customer_type = c.b2b ? 'b2b' : 'b2c';
    if (c.order_count !== undefined) d.order_count = c.order_count;
    if (c.total_spent !== undefined) d.lifetime_value = c.total_spent;
    if (c.new_customer !== undefined) d.is_new_customer = c.new_customer;
    if (c.first_name) d.first_name = c.first_name;
    if (c.last_name) d.last_name = c.last_name;
    if (c.street) d.street = c.street;
    if (c.city) d.city = c.city;
    if (c.state) d.state = c.state;
    if (c.postal_code) d.postal_code = c.postal_code;
    if (c.country) d.country = c.country;
    if (!d.country && window.theme.country_code) d.country = window.theme.country_code;
    return d;
  };

  /* ---- Cart data ---- */

  TrackingBase.prototype.getCartData = function() {
    if (!window.theme || !window.theme.cart || !window.theme.cart.items) {
      return { items: [], total_items: 0, total_value: 0 };
    }
    var cart = window.theme.cart;
    var self = this;
    return {
      items: cart.items.map(function(item) { return self.normalizeCartItem(item); }),
      total_items: cart.items.reduce(function(sum, item) { return sum + item.quantity; }, 0),
      total_value: cart.items.reduce(function(sum, item) { return sum + item.price * item.quantity; }, 0),
      currency: window.theme.currency
    };
  };

  TrackingBase.prototype.normalizeCartItem = function(item) {
    return {
      item_id: item.item_id || (item.product_id + '_' + (item.variant_id || '')),
      item_name: item.item_name,
      item_category: item.item_category,
      item_brand: item.item_brand,
      item_variant: item.item_variant,
      sku: item.item_sku,
      price: parseFloat(item.price || 0),
      quantity: parseInt(item.quantity || 1, 10),
      product_id: item.product_id,
      variant_id: item.variant_id
    };
  };

  /* ---- Queue ---- */

  TrackingBase.prototype.queueEvent = function(eventName, eventData, options) {
    if (!this.config.queueEnabled) return;
    if (this.eventQueue.length >= this.config.maxQueueSize) this.eventQueue.shift();
    this.eventQueue.push({ eventName: eventName, eventData: eventData, options: options, timestamp: Date.now() });
    this.log('Event queued: ' + eventName);
  };

  TrackingBase.prototype._processQueue = function() {
    if (this.eventQueue.length === 0) return;
    this.log('Processing ' + this.eventQueue.length + ' queued events');
    var self = this;
    (function next() {
      if (self.eventQueue.length === 0) return;
      var evt = self.eventQueue.shift();
      self._trackWithRetry(evt.eventName, evt.eventData, evt.options)
        .catch(function() { /* already logged */ })
        .then(next);
    })();
  };

  /* ---- Retry ---- */

  TrackingBase.prototype._trackWithRetry = function(eventName, eventData, options) {
    var self = this;
    var eventId = (options && options.eventID) || this.generateEventId();
    var attempts = this.config.retryAttempts;

    function attempt(n) {
      return Promise.resolve()
        .then(function() {
          return self.track(eventName, eventData, Object.assign({}, options, { eventID: eventId }));
        })
        .then(function() {
          self.pendingEvents.delete(eventId);
        })
        .catch(function(err) {
          if (n < attempts) {
            return new Promise(function(r) { setTimeout(r, self.config.retryDelay * n); })
              .then(function() { return attempt(n + 1); });
          }
          self.pendingEvents.delete(eventId);
          throw err;
        });
    }

    return attempt(1);
  };

  /* ---- Public trackEvent ---- */

  TrackingBase.prototype.trackEvent = function(eventName, eventData, options) {
    eventData = eventData || {};
    options = options || {};
    var self = this;

    if (!this.config.enabled) return Promise.resolve(null);

    /* Debounce */
    var debounceKey = eventName + '_' + JSON.stringify(eventData);
    var now = Date.now();
    if (this._eventDebounce.has(debounceKey)) {
      if (now - this._eventDebounce.get(debounceKey) < 500) return Promise.resolve(null);
    }
    this._eventDebounce.set(debounceKey, now);

    /* Cleanup old entries */
    if (this._eventDebounce.size > 100) {
      for (var entry of this._eventDebounce.entries()) {
        if (now - entry[1] > 60000) this._eventDebounce.delete(entry[0]);
      }
    }

    if (!options.eventID) options.eventID = this.generateEventId();

    eventData = this.normalizeEventData(eventData);

    var customerData = this.getCustomerData();
    if (Object.keys(customerData).length > 0) {
      eventData = Object.assign({}, eventData, customerData);
    }

    this.log('Tracking event: ' + eventName, { eventId: options.eventID });

    if (!this.isInitialized) {
      this.queueEvent(eventName, eventData, options);
      return Promise.resolve(options.eventID);
    }

    this.pendingEvents.add(options.eventID);
    return this._trackWithRetry(eventName, eventData, options)
      .catch(function(err) { self.error('Failed to track: ' + eventName, err); })
      .then(function() { return options.eventID; });
  };

  /* ---- Readiness ---- */

  TrackingBase.prototype.isReady = function() {
    return this.isInitialized && this.config.enabled && this.tagLoaded;
  };

  /* ---- Logging ---- */

  TrackingBase.prototype.log = function(msg, data) {
    if (!this.config.debug) return;
    if (data !== undefined && data !== null) {
      console.info('[' + this.name + '] ' + msg, data);
    } else {
      console.info('[' + this.name + '] ' + msg);
    }
  };

  TrackingBase.prototype.warn = function(msg, data) {
    if (data !== undefined && data !== null) {
      console.warn('[' + this.name + '] ' + msg, data);
    } else {
      console.warn('[' + this.name + '] ' + msg);
    }
  };

  TrackingBase.prototype.error = function(msg, err) {
    if (err !== undefined && err !== null) {
      console.error('[' + this.name + '] ' + msg, err);
    } else {
      console.error('[' + this.name + '] ' + msg);
    }
  };

  /* ---- AJAX event listeners ---- */

  TrackingBase.prototype._setupAjaxEventListeners = function() {
    document.addEventListener('ajax:response', this._handleAjaxEvent.bind(this));
    document.addEventListener('cart:updated', this._handleCartUpdated.bind(this));
  };

  TrackingBase.prototype._handleAjaxEvent = function(event) {
    var detail = event.detail || {};
    var url = detail.url;
    if (url && (url.indexOf('/cart/') !== -1 || url.indexOf('/search/') !== -1)) {
      this.log('Ajax event: ' + url);
    }
  };

  TrackingBase.prototype._handleCartUpdated = function(event) {
    var cartData = event.detail;
    if (cartData) {
      this.log('Cart updated');
    }
  };

  /* ---- DOM event listeners ---- */

  TrackingBase.prototype._attachEventListeners = function() {
    var self = this;

    document.addEventListener('click', function(e) {
      var target = e.target;

      /* Phone links */
      var phoneLink = target.closest('a[href^="tel:"]');
      if (phoneLink) {
        self.trackEvent('phone_clicked', { phone: phoneLink.getAttribute('href').replace('tel:', '') });
        return;
      }

      /* Email links */
      var emailLink = target.closest('a[href^="mailto:"]');
      if (emailLink) {
        self.trackEvent('email_clicked', { email: emailLink.getAttribute('href').replace('mailto:', '') });
        return;
      }
    }, true);

    document.addEventListener('submit', function(e) {
      var form = e.target.closest('form[action^="/contact"]');
      if (!form) return;

      var formTypeEl = form.querySelector('input[name="form_type"]');
      if (formTypeEl) {
        if (formTypeEl.value === 'customer') {
          self.trackEvent('newsletter_subscription', { form_location: window.location.href });
        } else if (formTypeEl.value === 'contact') {
          self.trackEvent('contact_form_submitted', { form_location: window.location.href });
        }
      }
    }, true);
  };

  /* ---- Cart refresh ---- */

  TrackingBase.prototype._refreshCartData = function() {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cartData) {
        if (window.theme) window.theme.cart = cartData;
      })
      .catch(function() { /* noop */ });
  };

  /* ---- Destroy ---- */

  TrackingBase.prototype.destroy = function() {
    this.config.enabled = false;
    this.isInitialized = false;
    this.eventQueue = [];
    this.pendingEvents.clear();
    this.log('Destroyed');
  };

  /* ================================================================
   *  TrackingIntegrationManager
   * ================================================================ */

  function TrackingIntegrationManager() {
    this.integrations = new Map();
  }

  TrackingIntegrationManager.prototype.register = function(name, integration) {
    this.integrations.set(name, integration);
    console.info('Integration registered: ' + name);
  };

  TrackingIntegrationManager.prototype.get = function(name) {
    return this.integrations.get(name);
  };

  TrackingIntegrationManager.prototype.trackAll = function(eventName, eventData, options) {
    var promises = [];
    for (var entry of this.integrations) {
      promises.push(
        entry[1].trackEvent(eventName, eventData, options || {}).catch(function() { return null; })
      );
    }
    return Promise.all(promises);
  };

  TrackingIntegrationManager.prototype.allReady = function() {
    for (var entry of this.integrations) {
      if (!entry[1].isReady()) return false;
    }
    return true;
  };

  TrackingIntegrationManager.prototype.destroy = function() {
    for (var entry of this.integrations) {
      entry[1].destroy();
    }
    this.integrations.clear();
  };

  /* ---- Exports ---- */

  window.TrackingBase = TrackingBase;
  window.TrackingIntegrationManager = TrackingIntegrationManager;
  window.trackingManager = new TrackingIntegrationManager();
})();
